import { useTranslation } from 'react-i18next';
import { Button } from '@folio/components/ui/button';
import { cn } from '@folio/lib';
import type { DocumentReviewFilter, DocumentReviewSummary } from './document-review';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { Badge } from '@folio/components/ui/badge';

export function DocumentReviewFilters({
  summary,
  filter,
  onFilterChange,
  showAttention = true,
}: {
  summary: DocumentReviewSummary;
  filter: DocumentReviewFilter;
  onFilterChange: (filter: DocumentReviewFilter) => void;
  showAttention?: boolean;
}) {
  const { t } = useTranslation();
  const counts = {
    all: summary.receivedCount,
    attention: summary.attentionCount,
    missing: summary.missingRequiredCount,
  };
  const options: DocumentReviewFilter[] = showAttention
    ? ['all', 'attention', 'missing']
    : ['all', 'missing'];

  return (
    <Stack gap={3}>
      <Group
        role="group"
        aria-label={t('documentReview.filterLabel')}
        aria-busy={summary.isLoading}
        wrap
        gap={2}
        className="border-b pb-4"
      >
        {options.map(option => (
          <Button
            key={option}
            type="button"
            variant="ghost"
            size="sm"
            disabled={summary.isLoading}
            aria-pressed={filter === option}
            onClick={() => onFilterChange(option)}
            className={cn('gap-2', filter === option && 'bg-accent text-primary')}
          >
            {t(`documentReview.filter.${option}`)}
            <Badge
              variant="light"
              size="sm"
              className="tabular-nums"
              tone={
                (option === 'missing' || option === 'attention') && counts[option] > 0
                  ? 'warning'
                  : 'muted'
              }
            >
              {summary.isLoading ? '–' : counts[option]}
            </Badge>
          </Button>
        ))}
      </Group>
      <p className="sr-only" role="status">
        {summary.isLoading
          ? t('documentReview.loading')
          : [
              t('documentReview.received', { count: summary.receivedCount }),
              t('documentReview.missing', { count: summary.missingRequiredCount }),
              ...(showAttention
                ? [t('documentReview.attention', { count: summary.attentionCount })]
                : []),
            ].join('. ')}
      </p>
      {showAttention && summary.processingCount > 0 && (
        <Text size="sm" tone="muted" role="status">
          {t('documentReview.processing', { count: summary.processingCount })}
        </Text>
      )}
      {summary.hasError && (
        <Text size="sm" tone="destructive" role="alert">
          {t('documentReview.loadError')}
        </Text>
      )}
    </Stack>
  );
}
